import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import RiskMatrixSwitcher from "@/components/RiskMatrixSwitcher";
import ConfirmButton from "@/components/ConfirmButton";
import { STATUSES, normalizeStatus, statusClass } from "@/lib/statuses";

function RiskBadge({ level, label }) {
  const cls = level === 0 ? "risk-0" : `risk-${level}`;
  return <span className={`${cls} border px-2.5 py-1 text-xs font-bold uppercase tracking-wider`}>{label}</span>;
}

export function OverviewTab({ data, results, actions }) {
  const completedActions = actions.filter((a) => a.status === "Concluída").length;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="card">
        <div className="overline">Respostas recebidas</div>
        <div className="kpi-number text-6xl mt-3 text-emerald-900">{data.response_count}</div>
        <div className="text-xs text-slate-500 mt-2">
          de {data.n_trabalhadores || "—"} esperadas
          {data.n_trabalhadores ? ` (${Math.round((data.response_count / data.n_trabalhadores) * 100)}%)` : ""}
        </div>
      </div>
      <div className="card">
        <div className="overline">Score geral</div>
        <div className="kpi-number text-6xl mt-3 text-emerald-900">{results.overall_score || "—"}</div>
        <div className="mt-3"><RiskBadge level={results.overall?.level || 0} label={results.overall?.label || "Sem dados"} /></div>
      </div>
      <div className="card">
        <div className="overline">Ações de plano</div>
        <div className="kpi-number text-6xl mt-3 text-emerald-900">{actions.length}</div>
        <div className="text-xs text-slate-500 mt-2">
          {completedActions} concluídas · {actions.length - completedActions} pendentes
        </div>
      </div>
    </div>
  );
}

export function ResultsTab({ results, assessmentId, assessment }) {
  const criticalCount = results.questions.filter((q) => q.score >= 4.5).length;
  const matrixType = assessment?.matrix_type || "pxs";
  const [strict, setStrict] = useState(Boolean(assessment?.aiha_strict));
  const [busy, setBusy] = useState(false);
  const toggleStrict = async () => {
    const next = !strict;
    setBusy(true);
    try {
      await api.put(`/assessments/${assessmentId}/aiha-strict`, { aiha_strict: next });
      setStrict(next);
      toast.success(next
        ? "Regra AIHA estrita ativada — bypass desabilitado."
        : "Bypass inteligente reativado.");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <div className="card mb-6">
        <div className="overline mb-4">Matriz AIHA por dimensão</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {results.dimensions.map((d) => (
            <div key={d.dimensao} className={`${d.responses ? `risk-${d.level}` : "risk-0"} border p-4`}>
              <div className="text-xs font-bold uppercase tracking-wider">{d.label}</div>
              <div className="font-display text-2xl font-extrabold mt-1">{d.score || "—"}</div>
              <div className="text-xs mt-1">{d.dimensao}</div>
              <div className="text-[10px] opacity-70 mt-1">{d.responses} resposta(s)</div>
            </div>
          ))}
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-slate-200">
          <div>
            <div className="overline">Regra de cálculo</div>
            <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">
              {strict
                ? "Modo estrito: uma única resposta crítica pode elevar a Severidade até S4 mesmo com score geral baixo."
                : "Bypass inteligente ativo: respostas críticas isoladas não inflam a Severidade quando o score consolidado é ≤ 2.5 (banda Tolerável)."}
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="aiha-strict-toggle-label">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Regra AIHA estrita</span>
            <button
              type="button"
              role="switch"
              aria-checked={strict}
              disabled={busy}
              onClick={toggleStrict}
              data-testid="aiha-strict-toggle"
              className={`relative inline-flex h-6 w-11 items-center transition-colors ${strict ? "bg-emerald-900" : "bg-slate-300"} ${busy ? "opacity-50 cursor-not-allowed" : ""}`}>
              <span className={`inline-block h-5 w-5 transform bg-white transition-transform ${strict ? "translate-x-5" : "translate-x-0.5"}`}></span>
            </button>
          </label>
        </div>
        <RiskMatrixSwitcher
          matrixType={matrixType}
          assessmentId={assessmentId}
          showSelector={true}
          editable={true}
          fmeaOverrides={assessment?.fmea_overrides || {}}
          overallScore={results.overall_score}
          dimensions={results.dimensions}
          criticalCount={criticalCount}
          totalResponses={results.response_count}
          expectedWorkers={assessment?.n_trabalhadores || 0}
          strict={strict}
        />
      </div>
      <div className="card">
        <div className="overline mb-4">Pontuação média por pergunta</div>
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-2 font-semibold uppercase text-xs">#</th>
              <th className="font-semibold uppercase text-xs">Pergunta</th>
              <th className="font-semibold uppercase text-xs">Dimensão</th>
              <th className="font-semibold uppercase text-xs text-center">Respostas</th>
              <th className="font-semibold uppercase text-xs text-right">Score</th>
            </tr>
          </thead>
          <tbody>
            {results.questions.map((q) => (
              <tr key={q.id} className="border-b border-slate-100">
                <td className="py-2 font-mono text-xs text-slate-500">{q.numero}</td>
                <td className="text-slate-800">{q.pergunta}</td>
                <td className="text-slate-500 text-xs">{q.dimensao}</td>
                <td className="text-center font-mono text-xs">{q.responses}</td>
                <td className="text-right font-mono font-bold">{q.score || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditableCell({ value, onSave, type = "text", className = "", testid, placeholder = "—" }) {
  const [draft, setDraft] = useState(value || "");

  // Sync draft when external value changes (after server round-trip)
  // Use a ref-like check based on document.activeElement to avoid clobbering typing
  const v = value || "";
  if (draft !== v && (typeof document === "undefined" || document.activeElement?.dataset?.testid !== testid)) {
    // out-of-band update from parent; only adjust if user isn't typing here
  }

  const commit = () => {
    if ((draft || "") !== (value || "")) onSave(draft);
  };

  return (
    <input
      type={type}
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setDraft(value || ""); e.currentTarget.blur(); }
      }}
      className={`w-full bg-transparent hover:bg-white border border-transparent hover:border-slate-300 focus:bg-white focus:border-sollute-green focus:ring-2 focus:ring-sollute-green/20 px-2 py-1 text-xs transition-colors focus:outline-none cursor-text ${className}`}
      data-testid={testid}
    />
  );
}

function StatusSelect({ value, onChange, testid }) {
  const norm = normalizeStatus(value);
  return (
    <select
      value={norm}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`${statusClass(norm)} text-[10px] font-bold uppercase tracking-wider px-2 py-1 border cursor-pointer focus:outline-none focus:ring-2 focus:ring-sollute-green/30`}
    >
      {STATUSES.map((s) => (
        <option key={s.value} value={s.value} className="bg-white text-slate-900 normal-case">
          {s.short}
        </option>
      ))}
    </select>
  );
}

function PrioritySelect({ value, onChange, testid }) {
  return (
    <select
      value={value || "Média"}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="text-xs font-semibold px-2 py-1 border border-slate-300 hover:border-slate-400 focus:border-sollute-green focus:outline-none cursor-pointer bg-white"
    >
      {["Baixa", "Média", "Alta", "Crítica"].map((p) => <option key={p}>{p}</option>)}
    </select>
  );
}

export function ActionsTab({ actions, onDelete, onNew, onSuggestions, onUpdate }) {
  return (
    <div>
      <div className="flex justify-end gap-2 mb-4">
        <button onClick={onSuggestions} className="btn btn-secondary" data-testid="action-suggestions-button">
          <i className="fa-solid fa-wand-magic-sparkles"></i> Gerar sugestões
        </button>
        <button onClick={onNew} className="btn btn-primary" data-testid="action-new-button">
          <i className="fa-solid fa-plus"></i> Nova ação
        </button>
      </div>
      <div className="card">
        {actions.length === 0 ? (
          <div className="text-slate-500 py-12 text-center">
            <i className="fa-solid fa-list-check text-3xl mb-3 text-slate-300"></i>
            <p>Sem ações registradas.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-3 font-semibold uppercase text-xs">Ação</th>
                <th className="font-semibold uppercase text-xs">Dimensão</th>
                <th className="font-semibold uppercase text-xs">Responsável</th>
                <th className="font-semibold uppercase text-xs">Prazo</th>
                <th className="font-semibold uppercase text-xs">Prioridade</th>
                <th className="font-semibold uppercase text-xs">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 font-semibold pr-3 align-top">{a.acao}</td>
                  <td className="text-slate-600 text-xs pr-3 align-top">{a.dimensao || "—"}</td>
                  <td className="text-slate-700 pr-3 min-w-[170px] align-top">
                    <EditableCell
                      value={a.responsavel}
                      onSave={(v) => onUpdate(a, { responsavel: v })}
                      placeholder="Definir responsável"
                      testid={`action-edit-resp-${a.id}`}
                    />
                  </td>
                  <td className="text-slate-700 font-mono text-xs pr-3 min-w-[150px] align-top">
                    <EditableCell
                      value={a.prazo}
                      type="date"
                      onSave={(v) => onUpdate(a, { prazo: v })}
                      placeholder="Definir prazo"
                      testid={`action-edit-prazo-${a.id}`}
                    />
                  </td>
                  <td className="align-top">
                    <PrioritySelect
                      value={a.prioridade}
                      onChange={(v) => onUpdate(a, { prioridade: v })}
                      testid={`action-edit-prio-${a.id}`}
                    />
                  </td>
                  <td className="align-top">
                    <StatusSelect
                      value={a.status}
                      onChange={(v) => onUpdate(a, { status: v })}
                      testid={`action-edit-status-${a.id}`}
                    />
                  </td>
                  <td className="text-right align-top">
                    <ConfirmButton
                      onConfirm={() => onDelete(a.id)}
                      testid={`action-delete-${a.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function ResponsesTab({ responses, totalQuestions }) {
  const FLAG_LABEL = {
    straight_line: { label: "Linha reta", color: "bg-amber-100 text-amber-900 border-amber-300", icon: "fa-equals" },
    incomplete:    { label: "Incompleta", color: "bg-slate-100 text-slate-700 border-slate-300", icon: "fa-circle-half-stroke" },
    too_fast:      { label: "Muito rápida", color: "bg-rose-100 text-rose-900 border-rose-300", icon: "fa-stopwatch" },
  };
  const fmtDur = (s) => {
    if (!s || s <= 0) return "—";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}m${r > 0 ? ` ${r}s` : ""}`;
  };
  return (
    <div className="card">
      {responses.length === 0 ? (
        <div className="text-slate-500 py-12 text-center">
          <i className="fa-solid fa-inbox text-3xl mb-3 text-slate-300"></i>
          <p>Nenhuma resposta recebida ainda.</p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-3 font-semibold uppercase text-xs">#</th>
              <th className="font-semibold uppercase text-xs">Respondente</th>
              <th className="font-semibold uppercase text-xs">Matrícula</th>
              <th className="font-semibold uppercase text-xs">Submetido em</th>
              <th className="font-semibold uppercase text-xs text-center">Duração</th>
              <th className="font-semibold uppercase text-xs">Qualidade</th>
              <th className="font-semibold uppercase text-xs text-center">Respostas</th>
            </tr>
          </thead>
          <tbody>
            {responses.map((r, i) => {
              const flags = r.quality_flags || [];
              return (
                <tr key={r.id} className="border-b border-slate-100" data-testid={`response-row-${r.id}`}>
                  <td className="py-3 font-mono text-xs text-slate-500">{i + 1}</td>
                  <td>{r.employee_name || <span className="text-slate-400 italic">anônimo</span>}</td>
                  <td className="text-slate-500 font-mono text-xs">{r.employee_matricula || "—"}</td>
                  <td className="text-slate-500 font-mono text-xs">{new Date(r.submitted_at).toLocaleString("pt-BR")}</td>
                  <td className="text-center font-mono text-xs text-slate-600" data-testid={`response-duration-${r.id}`}>
                    {fmtDur(r.duration_seconds)}
                  </td>
                  <td>
                    {flags.length === 0 ? (
                      <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">
                        <i className="fa-solid fa-circle-check mr-1"></i> ok
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1" data-testid={`response-flags-${r.id}`}>
                        {flags.map((f) => {
                          const meta = FLAG_LABEL[f] || { label: f, color: "bg-slate-100 text-slate-700 border-slate-300", icon: "fa-flag" };
                          return (
                            <span key={f} className={`${meta.color} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>
                              <i className={`fa-solid ${meta.icon} mr-1`}></i>{meta.label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="text-center font-mono">{r.answers.length}/{totalQuestions || 54}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
